/**
 * Audit Utilities - User display name resolution
 *
 * Provides functions to resolve user UUIDs to display names
 * using the resolve_user_display_name() RPC function.
 *
 * Includes a session-level cache to avoid redundant RPC calls.
 */

import { supabase } from '../supabase';
import { logger } from '../logger';

// Module-level cache for resolved display names
// Key: user UUID, Value: display name string
const displayNameCache = new Map<string, string>();

/**
 * Resolve a single user UUID to a display name.
 * Uses the resolve_user_display_name() RPC function.
 * Results are cached in-memory for the session.
 *
 * @param userId - The UUID of the user to resolve
 * @returns The display name, or null if resolution fails
 */
export async function resolveUserDisplayName(userId: string): Promise<string | null> {
  if (!userId) return null;

  // Check cache first
  const cached = displayNameCache.get(userId);
  if (cached !== undefined) {
    return cached;
  }

  try {
    const { data, error } = await supabase.rpc('resolve_user_display_name', {
      p_user_id: userId,
    });

    if (error) {
      logger.error('[audit] Error resolving display name:', error);
      return null;
    }

    const displayName = data as string | null;
    if (displayName) {
      displayNameCache.set(userId, displayName);
    }
    return displayName;
  } catch (err) {
    logger.error('[audit] Unexpected error resolving display name:', err);
    return null;
  }
}

/**
 * Resolve multiple user UUIDs to display names in a single batch.
 * Deduplicates UUIDs and uses the cache to avoid redundant calls.
 * Non-cached UUIDs are resolved via parallel RPC calls.
 *
 * @param userIds - Array of user UUIDs to resolve (may contain nulls/duplicates)
 * @returns Map of UUID -> display name
 */
export async function resolveUserDisplayNames(
  userIds: (string | null | undefined)[]
): Promise<Map<string, string>> {
  const result = new Map<string, string>();

  // Filter out nulls/undefined and deduplicate
  const uniqueIds = [...new Set(userIds.filter((id): id is string => !!id))];

  if (uniqueIds.length === 0) return result;

  // Separate cached vs uncached
  const uncachedIds: string[] = [];
  for (const id of uniqueIds) {
    const cached = displayNameCache.get(id);
    if (cached !== undefined) {
      result.set(id, cached);
    } else {
      uncachedIds.push(id);
    }
  }

  // Resolve uncached IDs in parallel
  if (uncachedIds.length > 0) {
    const promises = uncachedIds.map(async (id) => {
      const name = await resolveUserDisplayName(id);
      if (name) {
        result.set(id, name);
      }
    });

    await Promise.all(promises);
  }

  return result;
}

/**
 * Clear the display name cache.
 * Call this when the user switches organizations to prevent
 * stale data from a previous session.
 */
export function clearDisplayNameCache(): void {
  displayNameCache.clear();
  logger.log('[audit] Display name cache cleared');
}
